/**
 * Compute a % match between a candidate's parsed skills and a job's tech stack.
 * Case-insensitive; light normalisation (strip punctuation, collapse spaces).
 */

const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[._+#()/\\-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Common aliases so parsed CV terms line up with job tech_stack labels
const ALIASES = {
  "react js": "react",
  "reactjs": "react",
  "next js": "next.js",
  "nextjs": "next.js",
  "node js": "node.js",
  "nodejs": "node.js",
  "postgres": "postgresql",
  "postgre sql": "postgresql",
  "aws cloud": "aws",
  "amazon web services": "aws",
  "gcp": "google cloud",
  "spring": "spring boot",
  "k8s": "kubernetes",
  "js": "javascript",
  "ts": "typescript",
  "py": "python",
};

const canon = (s) => {
  const n = norm(s);
  return ALIASES[n] || n;
};

export function scoreJob(candidateSkills = [], jobTech = []) {
  if (!jobTech.length) {
    return { score: 0, matched: [], missing: [] };
  }
  const cand = new Set(candidateSkills.map(canon).filter(Boolean));
  const matched = [];
  const missing = [];
  for (const t of jobTech) {
    if (cand.has(canon(t))) matched.push(t);
    else missing.push(t);
  }
  const raw = (matched.length / jobTech.length) * 100;
  const score = Math.max(0, Math.min(100, Math.round(raw)));
  return { score, matched, missing };
}

/**
 * Bucket a raw score into a UX category.
 */
export function matchBadge(score) {
  if (score >= 70) return { label: "Strong match", tone: "strong" };
  if (score >= 40) return { label: "Good match", tone: "good" };
  if (score >= 15) return { label: "Partial match", tone: "partial" };
  return { label: "Explore", tone: "neutral" };
}

export function rankJobs(jobs, candidateSkills) {
  if (!candidateSkills?.length) {
    return jobs.map((j) => ({ ...j, _match: null }));
  }
  const scored = jobs.map((j) => {
    const m = scoreJob(candidateSkills, j.tech_stack || []);
    return { ...j, _match: m };
  });
  scored.sort((a, b) => (b._match?.score ?? 0) - (a._match?.score ?? 0));
  return scored;
}
